import { Fork, Invoke, nextPointer } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAccumulatedBuyInLimit from '../components/RMFAccumulatedBuyInLimit/RMFAccumulatedBuyInLimit';
import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFAutomaticWithdrawalLimit from '../components/RMFAutomaticWithdrawalLimit/RMFAutomaticWithdrawalLimit';
import RMFCompleted from '../components/RMFCompleted/RMFCompleted';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFEKYCSuccess from '../components/RMFEKYCSuccess/RMFEKYCSuccess';
import RMFGender from '../components/RMFGender/RMFGender';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPlaceOfBirth from '../components/RMFPlaceOfBirth/RMFPlaceOfBirth';
import RMFPokerTimeLimit from '../components/RMFPokerTimeLimit/RMFPokerTimeLimit';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFSummaryScreen from '../components/RMFSummaryScreen/RMFSummaryScreen';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfFRFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { invokeSignupDob, forkDobCheck, forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone, RMFVerification]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const invokeLegalNameSave = new Invoke(Service.RmfLegalNameSave, RMFGender, RMFLegalName);
  const invokeGenderSave = new Invoke(Service.RmfGenderSave, RMFPlaceOfBirth, RMFGender);

  const invokePlaceOfBirthSave = new Invoke(
    Service.RmfPlaceOfBirthSave,
    RMFAddress,
    RMFPlaceOfBirth
  );
  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFDepositLimit, RMFAddress);

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    RMFAccumulatedBuyInLimit,
    RMFDepositLimit
  );

  const invokeAccumulatedLimitSave = new Invoke(
    Service.RmfAccumulatedLimitSave,
    RMFPokerTimeLimit,
    RMFAccumulatedBuyInLimit
  );

  const invokePokerTimeSave = new Invoke(
    Service.RmfPokerTimeSave,
    RMFAutomaticWithdrawalLimit,
    RMFPokerTimeLimit
  );

  const retryMarker = {
    name: 'retryMarker',
    after: { 0: nextPointer }
  };

  const forkRetryExit = new Fork(
    Guard.CheckRetryUserFlags,
    [forkJumpToFeature],
    [RMFEKYCSuccess, forkSignupDepositCheckDone]
  );

  const forkStandaloneRMFCheck = new Fork(Guard.HideRMFCompletion, [forkRmfExit], RMFCompleted);

  const invokeRetryEnableRealMoneyFlag = new Invoke(
    Service.RmfRetryInvokeRealMoneyOk,
    forkStandaloneRMFCheck
  );

  const invokeRetryFRDataSave = new Invoke(
    Service.RmfRetryFRUserDataSave,
    invokeRetryEnableRealMoneyFlag
  );

  const forkEKYCErrorHandling = new Fork(
    Guard.CheckEKYCRetry,
    [RMFSummaryScreen, invokeRetryFRDataSave],
    [forkStandaloneRMFCheck]
  );

  const forkEKYCFailCheck = new Fork(
    Guard.CheckEKYCFailed,
    [forkEKYCErrorHandling],
    [forkStandaloneRMFCheck]
  );

  const invokeEnableRealMoneyFlag = new Invoke(Service.RmfEnableRealMoneyFlag, forkEKYCFailCheck);

  const invokeAutomaticWithdrawalLimitSave = new Invoke(
    Service.RmfAutomaticWithdrawalLimitSave,
    invokeEnableRealMoneyFlag,
    RMFAutomaticWithdrawalLimit
  );

  return [
    invokeSignupDob,
    forkDobCheck,
    invokeLegalNameSave,
    RMFGender,
    invokeGenderSave,
    RMFPlaceOfBirth,
    invokePlaceOfBirthSave,
    RMFAddress,
    invokeAddressSave,
    RMFDepositLimit,
    invokeDepositLimitSave,
    RMFAccumulatedBuyInLimit,
    invokeAccumulatedLimitSave,
    RMFPokerTimeLimit,
    invokePokerTimeSave,
    RMFAutomaticWithdrawalLimit,
    invokeAutomaticWithdrawalLimitSave,
    invokeEnableRealMoneyFlag,
    retryMarker,
    forkEKYCFailCheck,
    RMFSummaryScreen,
    invokeRetryFRDataSave,
    invokeRetryEnableRealMoneyFlag,
    forkStandaloneRMFCheck,
    RMFCompleted,
    forkRetryExit,
    forkRmfExit,
    jumpToFeatureStep
  ];
};

export default useRmfFRFlow;
